import { call, delay, put, select, take, takeEvery } from 'redux-saga/effects';

import type { SagaIterator } from '@redux-saga/types';

import { clientId as selectClientId } from 'app/lib/account/selectors';
import { mainHubInstance, nextDialogId, subscribeToDialogResponse } from 'app/lib/signalr';
import { promptOnStakeCashoutDecrease, promptOnStakeCashoutIncrease } from 'app/lib/site-configs/selectors';
import { CashoutErrorCode, StakeState } from 'app/lib/staking/constants';

import {
  CashoutStake,
  PROCESS_CASHOUT_STAKE, removeCashedOutStake,
  resetCashoutState,
  saveCashingOutStake,
  submitCashoutFailure,
  submitCashoutSuccess,
  updateStake,
} from './actions/cashout';
import { saveBigCashOutError } from './utils/big-cash-out-storage';

// ---

const CASHOUT_REQUEST_TIMEOUT = 1000;
const DISPLAY_RESULTS_TIMEOUT = 3500;

const errorResponse = () => ({ error: CashoutErrorCode.INTERNAL_SERVER_ERROR });

// ---

export function* StakeCashoutSaga(payload: CashoutStake['payload']): SagaIterator {
  let isSuccessfulRequest = false;

  const dialogId = nextDialogId();

  const allowIncrease = !(yield select(promptOnStakeCashoutIncrease));
  const allowDecrease = !(yield select(promptOnStakeCashoutDecrease));

  const preferences = { allowIncrease, allowDecrease };

  // TODO
  // В один прекрасный момент тут появится сокет на крупный выкуп.
  // Пока его нет, в файле src/modules/MyStakes/utils/cashout-reducer.ts есть маленький костыль
  // Найти его можно поискав по проекту такой текст: cashoutSaga.link
  const channel = yield call(subscribeToDialogResponse, 'cashout-stake-response', dialogId);

  let response;

  try {
    yield put(saveCashingOutStake(payload.id, dialogId));

    const cashOutData = {
      dialogId,
      preferences,
      stakeId: payload.id,
      amount: payload.amount,
    };

    const hubInstance = mainHubInstance.tryToGetHub();

    yield call([hubInstance, hubInstance.send], 'cashout', cashOutData);

    response = yield take(channel);

    if (response.payload.error) {
      throw {
        responseError: true,
        errorPayload: response.payload,
      };
    }

    isSuccessfulRequest = true;
    yield put(submitCashoutSuccess(response.payload, payload));
  }
  catch (err) {
    console.error('Stake cash out failed', err);

    const hasResponseErrorPayload = err instanceof Object && 'responseError' in err;
    const responsePayload = hasResponseErrorPayload ? response.payload : errorResponse();

    const clientId: string | undefined = yield select(selectClientId);

    if (clientId === undefined) {
      console.error('cashoutSaga -> failed to keep an error about big cash out.');
    }
    else {
      saveBigCashOutError(clientId, payload.id, responsePayload.error);
    }

    yield put(submitCashoutFailure(responsePayload, payload));
  }
  finally {
    yield put(removeCashedOutStake(payload.id));
    channel.close();
  }

  yield delay(DISPLAY_RESULTS_TIMEOUT); // let user to see the results

  if (isSuccessfulRequest) {
    yield put(updateStake({
      id: payload.id,
      state: StakeState.CASH_OUT,
      winning: response.payload.amount,
    }));
  }

  yield put(resetCashoutState(payload.id));
}

// ---

export default function* cashOutProcess(): SagaIterator {
  yield takeEvery(PROCESS_CASHOUT_STAKE, function* (action: CashoutStake) {
    yield delay(CASHOUT_REQUEST_TIMEOUT); // let user to see request animation
    yield call(StakeCashoutSaga, action.payload);
  });
}
